import * as React from "react";

import type { ProjectLogOut } from "@/entities/projects/types";
import { cn } from "@/shared/lib/cn";
import { parseUtc } from "@/shared/lib/format";

export interface ProjectLogProps {
  entries: ProjectLogOut[];
  emptyText?: string;
}

/** Моноширинный лог-стрим с автоскроллом вниз. */
export function ProjectLog({
  entries,
  emptyText = "Лог пока пустой",
}: ProjectLogProps): JSX.Element {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [entries.length]);

  return (
    <div className="glass-card overflow-hidden p-0">
      <div className="border-b border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Лог
      </div>
      <div
        ref={ref}
        className="max-h-80 overflow-y-auto px-4 py-3 font-mono text-xs leading-relaxed"
      >
        {entries.length === 0 ? (
          <div className="text-muted-foreground">{emptyText}</div>
        ) : (
          entries.map((e, i) => (
            <div key={i} className="flex gap-2 whitespace-pre-wrap break-words">
              <span className="shrink-0 text-muted-foreground">[{shortTime(e.ts)}]</span>
              <span
                className={cn(
                  e.level === "error" && "text-pink",
                  e.level === "warning" && "text-amber",
                  e.level !== "error" && e.level !== "warning" && "text-foreground/85",
                )}
              >
                {e.message}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function shortTime(iso: string): string {
  const d = parseUtc(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes(),
  ).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
}
